import { ApiQueryParams } from '@frontend/api-utils';
import { Logger } from '@frontend/logger';
import { SettingKey, SettingValue } from '@frontend/setting/types';
import { TransactionUserClient } from '@frontend/transaction/api';
import { TransactionUser, TransactionUserType } from '@frontend/transaction/types';
import { UserClient } from '@frontend/user/api';
import { UserListResponse, UserQueryParams } from '@frontend/user/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

const toBooleanSetting = (value: SettingValue | undefined): boolean => value === true || value === 'true' || value === 1 || value === '1';

const DELAYED_RECEIVERS_MIN_SEARCH_CHARACTERS = 3;
const RECEIVER_PAGE_SIZE = '100';

interface ViewProps {
    isLoading?: boolean;
    selectedReceivers: (string | null)[];
    delayedReceiversSearch: boolean;
    fetchReceiverOptions: (queryParams?: ApiQueryParams<UserQueryParams>) => Promise<UserListResponse>;
    addReceiver: () => void;
    removeReceiver: (index: number) => void;
    updateReceiver: (index: number, receiver: string | null) => void;
    canContinue: boolean;
    onSubmit: () => void;
    onBack: (screenType: ScreenType) => void;
}

const useFablabSelectReceivers = (): ViewProps => {
    const { store, onBack, onSubmit: onContinue, updateWizardData } = useScreen();
    const { transaction } = useWizardTransaction({
        wizardId: store?.wizard.id,
        store,
        autoCreate: true
    });
    const [isLoading, setIsLoading] = useState(true);
    const [selectedReceivers, setSelectedReceivers] = useState<(string | null)[]>([null]);
    const selectedReceiversRef = useRef(selectedReceivers);

    const storeReceivers = (store?.data?.receivers as TransactionUser[]) || [];
    const remoteReceiversRef = useRef<TransactionUser[]>([]);

    const delayedReceiversSearch = toBooleanSetting(store?.functionalitySettings?.find((fs) => fs.key === SettingKey.DELAYED_RECEIVERS_SEARCH)?.value);

    useEffect(() => {
        selectedReceiversRef.current = selectedReceivers;
    }, [selectedReceivers]);

    const writeReceivers = (receiverIds: string[]) => {
        if (!store) return;

        const newReceivers: Partial<TransactionUser>[] = receiverIds.map((userId) => ({
            user_id: userId,
            type: TransactionUserType.RECEIVER,
            transaction_id: store.data?.transaction?.id || '',
            account_id: store.wizard.account_id,
            creation_timestamp: new Date().toISOString(),
            update_timestamp: new Date().toISOString()
        }));

        updateWizardData({
            ...store.data,
            receivers: newReceivers
        });
    };

    const syncReceiversAndAdvance = async (receiverIds: string[]) => {
        if (!store || !transaction?.id) return;

        const accountId = store.wizard.account_id;
        const transactionId = transaction.id;
        const remoteReceivers = remoteReceiversRef.current;

        const receiversToAdd = receiverIds.filter((local) => !remoteReceivers.find((remote) => remote.user_id === local));
        const receiversToRemove = remoteReceivers.filter((remote) => !receiverIds.find((local) => local === remote.user_id));

        const addedReceivers: TransactionUser[] = [];
        for (const receiver of receiversToAdd) {
            const addedReceiver = await TransactionUserClient.postTransactionUser(accountId, transactionId, {
                user_id: receiver,
                type: TransactionUserType.RECEIVER
            });
            addedReceivers.push(addedReceiver);
        }

        for (const receiver of receiversToRemove) {
            await TransactionUserClient.deleteTransactionUser(accountId, transactionId, receiver.user_id);
        }

        remoteReceiversRef.current = [
            ...addedReceivers,
            ...storeReceivers.filter((local) => remoteReceivers.find((remote) => remote.user_id === local.user_id))
        ] as TransactionUser[];

        const nextStep = store.steps.find((step) => step.screen === ScreenType.FABLAB_SELECT_RECEIVERS && step.trigger === ScreenTrigger.SUBMIT);
        onContinue(nextStep);
    };

    useEffect(() => {
        if (!store) {
            setIsLoading(false);
            return;
        }

        const loadReceivers = async () => {
            try {
                if (transaction?.id) {
                    const fetchedReceivers = await TransactionUserClient.fetchTransactionUsers({
                        transaction_id: transaction.id,
                        type: TransactionUserType.RECEIVER
                    });
                    remoteReceiversRef.current = fetchedReceivers.results;

                    if (storeReceivers.length === 0 && fetchedReceivers.results.length > 0) {
                        updateWizardData({
                            ...store.data,
                            receivers: fetchedReceivers.results
                        });
                        setSelectedReceivers(fetchedReceivers.results.map((receiver) => receiver.user_id));
                    }
                }

                if (storeReceivers.length > 0) {
                    setSelectedReceivers(storeReceivers.map((receiver) => receiver.user_id));
                }
            } catch (error) {
                Logger.error('Failed to load FabLab receivers', error);
            } finally {
                setIsLoading(false);
            }
        };

        void loadReceivers();
    }, []);

    const addReceiver = () => {
        setSelectedReceivers([...selectedReceiversRef.current, null]);
    };

    const removeReceiver = (index: number) => {
        setSelectedReceivers(selectedReceiversRef.current.filter((_, i) => i !== index));
    };

    const updateReceiver = (index: number, receiver: string | null) => {
        const updated = [...selectedReceiversRef.current];
        updated[index] = receiver;
        setSelectedReceivers(updated);
        writeReceivers(updated.filter((id): id is string => id !== null));
    };

    const fetchReceiverOptions = useCallback(
        (queryParams?: ApiQueryParams<UserQueryParams>): Promise<UserListResponse> => {
            if (!store) {
                return Promise.resolve({ count: 0, results: [] });
            }

            const search = typeof queryParams?.search === 'string' ? queryParams.search.trim() : '';

            if (delayedReceiversSearch && search.length < DELAYED_RECEIVERS_MIN_SEARCH_CHARACTERS) {
                return Promise.resolve({ count: 0, results: [] });
            }

            const { search: _omitSearch, ...restQueryParams } = queryParams ?? {};

            return UserClient.fetchUsers({
                ...restQueryParams,
                account_id: store.wizard.account_id,
                index: String(queryParams?.index ?? '0'),
                size: RECEIVER_PAGE_SIZE,
                sort_field: 'first_name',
                sort_direction: 'asc',
                ...(search ? { search } : {})
            });
        },
        [store, delayedReceiversSearch]
    );

    const canContinue = selectedReceivers.some((receiver) => receiver !== null) && selectedReceivers.length > 0;

    const onSubmit = async () => {
        if (!store || !canContinue || !transaction?.id) return;

        setIsLoading(true);
        try {
            const relevantReceivers = selectedReceiversRef.current.filter((receiver): receiver is string => receiver !== null);
            await syncReceiversAndAdvance(relevantReceivers);
        } finally {
            setIsLoading(false);
        }
    };

    return {
        isLoading,
        selectedReceivers,
        delayedReceiversSearch,
        fetchReceiverOptions,
        addReceiver,
        removeReceiver,
        updateReceiver,
        canContinue,
        onSubmit,
        onBack
    };
};

export default useFablabSelectReceivers;
